/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, TextField } from "@mui/material";
import debounce from "lodash.debounce";
import WebpageEditContext from "components/webpage-selector/context";
import LabelledField from "components/labelled-field";
import { SVGIcons } from "cms/icons/svg-icons";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { ImageInPageEditorV2 } from "cms/images/image-inpage-editor-v2";
import { cloneObjectsArray } from "utils/array";
import { AddImageButton } from "./add-image-button";

const WebpageDisplay = React.memo((props) => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const webpageEditContext = useContext(WebpageEditContext);
    const onChange = webpageEditContext?.onChange;
    const onRemove = webpageEditContext?.onRemove;
    const webpage = props?.webpage || webpageEditContext?.webpage;
    
    // Card props for updating card data
    const card = props?.card;
    const index = props?.index;
    const cardsList = props?.cardsList;
    const externalCardsListRef = props?.cardsListRef;
    const updateCards = props?.updateCards;

    const [model, setModel] = useState();
    const [title, setTitle] = useState(card?.title || model?.title);
    const [description, setDescription] = useState(card?.description || model?.description);
    
    // Use refs to track if we're currently editing to prevent sync conflicts
    const isEditingTitleRef = useRef(false);
    const isEditingDescriptionRef = useRef(false);
    const lastCardTitleRef = useRef(card?.title);
    const lastCardDescriptionRef = useRef(card?.description);
    
    // Use refs to store latest cardsList and updateCards to prevent callback recreation
    const cardsListRef = useRef(cardsList);
    const updateCardsRef = useRef(updateCards);
    
    // Update refs when props change
    useEffect(() => {
        cardsListRef.current = cardsList;
        updateCardsRef.current = updateCards;
    }, [cardsList, updateCards]);

    const getCurrentCardsList = useCallback(() => {
        return externalCardsListRef?.current || cardsListRef.current;
    }, [externalCardsListRef]);

    useEffect(() => {
        const loadModel = async () => {
            if (!webpage?.model) return;
            
            const model = await SimpliCityAdminSDK.model.findOne(token, webpage.model);
            setModel(model);
            
            // Initialize title and description from card if available, otherwise from model
            const newTitle = card?.title || model?.title;
            const newDescription = card?.description || model?.description;
            setTitle(newTitle);
            setDescription(newDescription);
            lastCardTitleRef.current = newTitle;
            lastCardDescriptionRef.current = newDescription;
            
            // Update card with model values if card title/description is empty or doesn't exist
            if (cardsList && updateCards && index !== undefined && model) {
                const updatedCards = cloneObjectsArray(cardsList);
                const updatedCard = { ...(card || {}) };
                let shouldUpdate = false;
                
                if (!card?.title && model?.title) {
                    updatedCard.title = model.title;
                    shouldUpdate = true;
                }
                
                if (!card?.description && model?.description) {
                    updatedCard.description = model.description;
                    shouldUpdate = true;
                }
                
                // Update card with banner_image from model if card doesn't have it
                if (!card?.banner_image && model?.banner_image) {
                    updatedCard.banner_image = model.banner_image;
                    shouldUpdate = true;
                }
                
                if (shouldUpdate) {
                    updatedCards[index] = updatedCard;
                    updateCards?.(updatedCards);
                }
            }
        };
        loadModel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, webpage?.model, webpage?.guid]); // Only depend on model ID and guid, not card properties

    // Sync local state with card props when they change externally (only if not currently editing)
    useEffect(() => {
        if (!isEditingTitleRef.current && card?.title !== undefined && card.title !== title && card.title !== lastCardTitleRef.current) {
            setTitle(card.title);
            lastCardTitleRef.current = card.title;
        }
    }, [card?.title, title]);

    useEffect(() => {
        if (!isEditingDescriptionRef.current && card?.description !== undefined && card.description !== description && card.description !== lastCardDescriptionRef.current) {
            setDescription(card.description);
            lastCardDescriptionRef.current = card.description;
        }
    }, [card?.description, description]);

    // Use refs to store latest title and description values for debounced handlers
    const titleRef = useRef(title);
    const descriptionRef = useRef(description);
    
    // Update refs when state changes
    useEffect(() => {
        titleRef.current = title;
    }, [title]);
    
    useEffect(() => {
        descriptionRef.current = description;
    }, [description]);

    // Debounced function to update card title
    const debouncedUpdateTitle = useRef(
        debounce((newTitle) => {
            isEditingTitleRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest description from ref to prevent losing pending updates
                updatedCards[index] = {
                    ...currentCard,
                    title: newTitle,
                    description: descriptionRef.current ?? currentCard.description,
                };
                lastCardTitleRef.current = newTitle;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    // Debounced function to update card description
    const debouncedUpdateDescription = useRef(
        debounce((newDescription) => {
            isEditingDescriptionRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest title from ref to prevent losing pending updates

                updatedCards[index] = {
                    ...currentCard,
                    title: titleRef.current ?? currentCard.title,
                    description: newDescription,
                };
                lastCardDescriptionRef.current = newDescription;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    const onChangeTitle = (event) => {
        const newTitle = event?.target?.value;
        isEditingTitleRef.current = true;
        setTitle(newTitle);
        titleRef.current = newTitle;
        // Debounce the card update
        debouncedUpdateTitle(newTitle);
    };

    const onChangeDescription = (event) => {
        const newDescription = event?.target?.value;
        isEditingDescriptionRef.current = true;
        setDescription(newDescription);
        descriptionRef.current = newDescription;
        // Debounce the card update
        debouncedUpdateDescription(newDescription);
    };

    // Get image GUID from banner_image object (banner_image.image contains the GUID)
    const getImageGuid = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's a string (old format), return it
        if (typeof bannerImageObj === "string") return bannerImageObj;
        // If it's an object, extract the image GUID
        return bannerImageObj?.image || null;
    }, []);
    
    // Get settings from banner_image object
    const getSettings = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's an object with settings, return settings
        if (typeof bannerImageObj === "object" && bannerImageObj?.settings) {
            return bannerImageObj.settings;
        }
        return null;
    }, []);
    
    // Get banner_image data for ImageInPageEditorV2 - memoize to prevent re-renders
    const bannerImageObj = useMemo(() => card?.banner_image || model?.banner_image, [card?.banner_image, model?.banner_image]);
    const imageGuid = useMemo(() => getImageGuid(bannerImageObj), [bannerImageObj, getImageGuid]);
    const settings = useMemo(() => getSettings(bannerImageObj), [bannerImageObj, getSettings]);
    
    // ImageInPageEditorV2 handlers - memoize to prevent re-renders
    // Use refs instead of direct dependencies to prevent recreation when cardsList changes
    const onChangeImageWithSettings = useCallback((imageObj, newSettings) => {
        const imageGuidValue = imageObj?.guid || "";
        
        // Update card with banner_image object structure
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            const currentBannerImage = currentCard?.banner_image;
            
            updatedCards[index] = {
                ...currentCard,
                banner_image: {
                    image: imageGuidValue,
                    settings: newSettings || currentBannerImage?.settings || {
                        format: "image.format.cropped-focus",
                        options: {
                            method: "100Aspect",
                            position: "center center",
                            dimension: 400,
                            useCaption: false
                        }
                    }
                },
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    const onRemoveImage = useCallback(() => {
        // Remove banner_image from card
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            updatedCards[index] = {
                ...currentCard,
                banner_image: null,
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    // Memoize ImageInPageEditorV2 props to prevent unnecessary re-renders
    const imageEditorProps = useMemo(() => ({
        image_guid: imageGuid,
        settings: settings,
        onChangeImageWithSettings: onChangeImageWithSettings,
        onRemoveImage: onRemoveImage,
        className: "webpage-display-image-inpage-editor-v2",
        AddButtonComponent: AddImageButton,
    }), [imageGuid, settings, onChangeImageWithSettings, onRemoveImage]);

    return (
        <Fragment>
            {webpage && webpage?.guid && (
                <div className='webpage-editor'>
                    <div className='webpage-editor-view'>
                        <div className='webpage-display'>
                            <div className='webpage-actions'>
                                <div className="card-webpage-display-webpage-selector-content-icon">
                                    <SVGIcons.common.Page />
                                </div>
                                <div className="card-webpage-display-webpage-selector-content-info">
                                    <div className="card-webpage-display-webpage-selector-content-info-title">
                                        {webpage?.nav_title}
                                    </div>
                                    <div className="card-webpage-display-webpage-selector-content-info-path">
                                        {webpage?.path}
                                    </div>
                                </div>
                                <div className='card-webpage-display-webpage-selector-content-actions'>
                                    <div className='image-action-change'>
                                        <IconButton onClick={onChange}>
                                            <SVGIcons.common.Edit />
                                        </IconButton>
                                    </div>
                                    <div className='image-action-remove'>
                                        <IconButton onClick={onRemove}>
                                            <SVGIcons.common.Close />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                            
                            <LabelledField label={t?.("commons.title")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='webpage_title'
                                    fullWidth={true}
                                    value={title || model?.title}
                                    onChange={onChangeTitle}
                                />
                            </LabelledField>
                            <LabelledField label={t?.("commons.description")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='webpage_url'
                                    fullWidth={true}
                                    multiline={true}
                                    maxRows={4}
                                    value={description || model?.description}
                                    onChange={onChangeDescription}
                                />
                            </LabelledField>

                            {/* Banner Image Editor */}
                            <LabelledField label="Image" style={{ marginTop: 12 }}>
                                <ImageInPageEditorV2 {...imageEditorProps} />
                            </LabelledField>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}, (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    const prevBannerImageGuid = prevProps.card?.banner_image?.image || 
        (typeof prevProps.card?.banner_image === "string" ? prevProps.card?.banner_image : null);
    const nextBannerImageGuid = nextProps.card?.banner_image?.image || 
        (typeof nextProps.card?.banner_image === "string" ? nextProps.card?.banner_image : null);
    
    return (
        prevProps.index === nextProps.index &&
        prevProps.webpage?.guid === nextProps.webpage?.guid &&
        prevBannerImageGuid === nextBannerImageGuid &&
        prevProps.card?.title === nextProps.card?.title &&
        prevProps.card?.description === nextProps.card?.description
    );
});

WebpageDisplay.displayName = "WebpageDisplay";
export { WebpageDisplay };